'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import { isSubclass } from '@/editor/core/classHelpers';

/**
 * Creates a reactive select property for classes that extend a given base class.
 * The options list automatically updates when new classes are registered in the ClassRegistry.
 *
 * @param baseClass - The base class to filter by (e.g., ENGINE.GameMode, ENGINE.Actor)
 * @param propertyName - Name for the property type (e.g., 'GameModeSelectProperty')
 * @param includeNone - Whether to include a 'None' option (default: true)
 * @returns A MobX State Tree model for the class select property
 *
 * @example
 * ```typescript
 * // Create a GameMode selector
 * const GameModeSelectProperty = createClassSelectProperty(
 *   ENGINE.GameMode,
 *   'GameModeSelectProperty'
 * );
 *
 * // Create an Actor selector
 * const ActorClassSelectProperty = createClassSelectProperty(
 *   ENGINE.Actor,
 *   'ActorClassSelectProperty'
 * );
 *
 * // Create a Component selector without 'None' option
 * const ComponentClassSelectProperty = createClassSelectProperty(
 *   ENGINE.SceneComponent,
 *   'ComponentClassSelectProperty',
 *   false
 * );
 * ```
 */
/** Sentinel value meaning "no class selected" in a class-select property. */
export const NO_CLASS_SELECTED = 'None' as const;

export function createClassSelectProperty<TBase extends new (...args: any[]) => any>(
  baseClass: TBase,
  propertyName: string,
  includeNone: boolean = true
) {
  return t.compose(propertyName, PropertyBase,
    t.model({
      type: t.literal(PropertyType.Select),
      value: t.optional(t.string, ''),
      defaultValue: t.optional(t.string, ''),
      placeholder: t.optional(t.string, '')
    })
  ).views(self => ({
    /**
     * Computed view that dynamically fetches classes from ClassRegistry.
     * This makes the options list reactive - it will automatically update when
     * new classes are registered (e.g., after loading a project).
     */
    get options(): Array<{ label: string; value: string }> {
      const registry = ENGINE.ClassRegistry.getRegistry();
      const options: Array<{ label: string; value: string }> = [];

      if (includeNone) {
        options.push({ label: 'None', value: NO_CLASS_SELECTED });
      }

      // Get all registered classes
      for (const [className, constructor] of registry.entries()) {
        // Check if it's a subclass of the base class
        if (isSubclass(constructor, baseClass)) {
          // Extract the short name (e.g., "ENGINE.GameMode" -> "GameMode")
          const shortName = className.split('.').pop() || className;
          options.push({
            label: shortName,
            value: className
          });
        }
      }

      return options;
    }
  })).actions(self => ({
    setValue(value: string, fromPanel = true, initial = false) {
      if (!self.options.some(opt => opt.value === value)) {
        console.warn(`Invalid ${propertyName} option value: ${value}`);
        return;
      }
      if (value === self.value) return;
      self.value = value;
      self.notifyValueChange(self.path, value, { fromPanel, initial, property: self as any });
    },
    setDefaultValue(value: string) {
      // Only validate if options are available
      if (self.options.length > 0 && !self.options.some(opt => opt.value === value)) {
        console.warn(`Invalid ${propertyName} option value for default: ${value}. Available options:`, self.options.map(o => o.value));
        return;
      }
      self.defaultValue = value;
    }
  }));
}

